import { useCallback, useEffect, useRef, useState } from 'react';
import {
  HANDOFF_MS,
  INTRO_DURATION_MS,
  INTRO_MODE,
  LOGIN_TIMELINE,
  markIntroPlayed,
  prefersReducedMotion,
  resolveIntroMode,
} from './primeSummoningGate.js';
import './primeSummoning.css';

/**
 * The PRIME alignment summon for the sign-in page.
 *
 * Plays the ~3.4s sequence on a small stage over the hero's mark slot, then
 * **hands off**: the canvas fades out over `HANDOFF_MS` while the hero's vector
 * mark fades in over the same box, and the WebGL context is torn down.
 *
 * The hand-off is the important part of the design. The settled mark is real
 * SVG, not a parked WebGL frame — so it is crisp at any device pixel ratio,
 * carries the exact brand colours with no tone mapping or film grain in the
 * way, scrolls and persists like any other element, and costs nothing once the
 * sequence is over. The scene resolves to the same picture pixel for pixel
 * (both are built from `primeMarkGeometry.js`), so the swap is invisible.
 *
 * It never blocks sign-in: the layer takes no pointer events, and the first
 * keystroke or click anywhere cuts straight to the mark. The three.js scene is
 * loaded with a dynamic import so it lands in its own chunk and never enters
 * the eager sign-in bundle.
 */
export function PrimeSummoningIntro({ slotRef, enabled = true, onModeResolved, onSettled }) {
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const [mode, setMode] = useState(null);
  const [canvasVisible, setCanvasVisible] = useState(false);
  /** True once the canvas has begun handing off to the vector mark. */
  const [handingOff, setHandingOff] = useState(false);
  /** Drops the canvas entirely once the cross-fade has finished. */
  const [done, setDone] = useState(false);
  // Surfaced on the layer so the sequence can be observed from a spec or the
  // console without reaching into the scene.
  const [phase, setPhase] = useState('idle');

  // Decide once, on mount, before any WebGL work happens.
  useEffect(() => {
    let alive = true;
    (async () => {
      const { supportsWebGL } = await import('./primeSummoningScene.js').catch(() => ({
        supportsWebGL: () => false,
      }));
      if (!alive) return;
      const next = resolveIntroMode({
        storage: typeof window === 'undefined' ? null : window.sessionStorage,
        reduceMotion: prefersReducedMotion(),
        webglAvailable: typeof supportsWebGL === 'function' ? supportsWebGL() : false,
        enabled,
      });
      setMode(next);
      onModeResolved?.(next);
      // Anything other than a full summon means the mark is the page's job from
      // the first paint — say so immediately rather than after a timer.
      if (next !== INTRO_MODE.FULL) onSettled?.(true);
    })();
    return () => {
      alive = false;
    };
  }, [enabled, onModeResolved, onSettled]);

  const settle = useCallback(() => {
    setHandingOff(true);
    onSettled?.(true);
  }, [onSettled]);

  useEffect(() => {
    // Only the full summon needs a GPU. Reduced motion, a repeat visit and a
    // machine with no WebGL all just show the vector mark, which the hero is
    // already rendering.
    if (mode !== INTRO_MODE.FULL || done) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    let alive = true;
    let scene = null;
    const cleanups = [];

    (async () => {
      const mod = await import('./primeSummoningScene.js');
      if (!alive) return;

      scene = mod.createPrimeSummoning(canvas, {
        timeline: LOGIN_TIMELINE,
        interactive: false,
        // The stage is small and short-lived, so full device resolution is
        // affordable and the frames stay crisp on a high-DPI display.
        maxPixelRatio: 2,
        maxBackingPixels: 700_000,
        // Over a live form, heavy grain reads as noise and strong chromatic
        // aberration fringes the wordmark beside it.
        grain: 0.22,
        aberration: 0.35,
        saturation: 1.16,
        // Nothing to keep alive: the canvas is unmounted at the hand-off.
        idleWhenResting: false,
        onPhase: (next) => {
          if (!alive) return;
          setPhase(next);
        },
      });
      sceneRef.current = scene;

      // The stage is a bounded box centred on the mark slot, big enough for the
      // frames to drift through, rather than the whole viewport. Fragment cost
      // dominates, so bounding it keeps the frame time roughly the same on a
      // laptop and on a large monitor.
      let lastStage = null;
      const syncFrame = () => {
        if (!alive || !scene || !canvas) return;
        const slot = slotRef?.current?.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        let cx;
        let cy;
        let stageW;
        if (slot && slot.width > 0 && slot.height > 0) {
          cx = slot.left + slot.width / 2;
          cy = slot.top + slot.height / 2;
          // Room around the mark for the drift, which runs a little over twice
          // its width at full misalignment.
          stageW = Math.min(Math.max(slot.width * 3.2, 260), 480, vw);
        } else {
          cx = vw < 900 ? vw / 2 : vw * 0.26;
          cy = vh * 0.5;
          stageW = Math.min(Math.max(vw * 0.24, 260), 480);
        }
        const stageH = Math.min(stageW, vh);

        // Clamp the stage inside the viewport. The hero sits close to the left
        // edge, so an unclamped stage centred on it would render part of its
        // pixels off-screen.
        // Rounded before the rect below is derived from it, not after: the
        // canvas is positioned in whole pixels, so deriving the mark's rect
        // from the unrounded value lands the summon up to half a pixel off the
        // slot the vector mark occupies, and the hand-off shifts.
        const left = Math.round(Math.max(0, Math.min(cx - stageW / 2, vw - stageW)));
        const top = Math.round(Math.max(0, Math.min(cy - stageH / 2, vh - stageH)));

        canvas.style.left = `${left}px`;
        canvas.style.top = `${top}px`;
        canvas.style.width = `${Math.round(stageW)}px`;
        canvas.style.height = `${Math.round(stageH)}px`;

        // Because the stage can be clamped, the mark is not necessarily at its
        // centre — pin it to the slot's position *within the stage*.
        const rect =
          slot && slot.width > 0 && slot.height > 0
            ? { x: slot.left - left, y: slot.top - top, width: slot.width, height: slot.height }
            : null;

        const sizeChanged = !lastStage || lastStage.w !== stageW || lastStage.h !== stageH;
        const rectChanged = !lastStage || JSON.stringify(lastStage.rect) !== JSON.stringify(rect);
        if (sizeChanged) scene.resize(stageW, stageH);
        if (sizeChanged || rectChanged) scene.setRestingRect(rect);
        lastStage = { w: stageW, h: stageH, rect };
      };
      syncFrame();

      // The stage is position:fixed, so it must be re-pinned as the page
      // scrolls or the summon detaches from the hero and drifts over the copy.
      let scrollRaf = 0;
      const onViewportChange = () => {
        if (scrollRaf) return;
        scrollRaf = requestAnimationFrame(() => {
          scrollRaf = 0;
          syncFrame();
        });
      };
      window.addEventListener('resize', onViewportChange);
      // Capture phase: scroll does not bubble, and the sign-in page deliberately
      // has no document scroll — the hero and the sign-in rail are their own
      // scrollports. Listening on the capture phase catches those too, so the
      // summon stays pinned to the slot however the slot came to move.
      window.addEventListener('scroll', onViewportChange, { passive: true, capture: true });
      cleanups.push(() => {
        window.removeEventListener('resize', onViewportChange);
        window.removeEventListener('scroll', onViewportChange, { capture: true });
        if (scrollRaf) cancelAnimationFrame(scrollRaf);
      });

      // The hero can reflow after fonts land; re-pin the slot when it does.
      if (typeof ResizeObserver !== 'undefined' && slotRef?.current) {
        const ro = new ResizeObserver(onViewportChange);
        ro.observe(slotRef.current);
        cleanups.push(() => ro.disconnect());
      }

      setCanvasVisible(true);
      markIntroPlayed(window.sessionStorage);
      const settleTimer = window.setTimeout(settle, INTRO_DURATION_MS);
      cleanups.push(() => window.clearTimeout(settleTimer));
    })();

    // Any interaction hands off immediately — a tester who just wants to sign
    // in must never wait for an animation.
    const cutShort = () => settle();
    const opts = { passive: true, once: true };
    window.addEventListener('keydown', cutShort, opts);
    window.addEventListener('pointerdown', cutShort, opts);
    cleanups.push(() => {
      window.removeEventListener('keydown', cutShort);
      window.removeEventListener('pointerdown', cutShort);
    });

    return () => {
      alive = false;
      for (const off of cleanups) off();
      sceneRef.current?.dispose();
      sceneRef.current = null;
    };
  }, [mode, slotRef, settle, done]);

  // Tear the canvas and its WebGL context down once the cross-fade has run.
  // Unmounting is what actually releases the context; leaving a settled canvas
  // in the tree is what made the previous build hold a GPU surface behind the
  // sign-in form for as long as the page stayed open.
  useEffect(() => {
    if (!handingOff || done) return undefined;
    const t = window.setTimeout(() => setDone(true), HANDOFF_MS);
    return () => window.clearTimeout(t);
  }, [handingOff, done]);

  if (mode !== INTRO_MODE.FULL || done) return null;

  return (
    <div
      className="prime-summon-layer"
      data-testid="prime-summoning-layer"
      data-mode={mode}
      data-handoff={handingOff ? 'true' : 'false'}
      data-phase={phase}
      aria-hidden="true"
    >
      <canvas
        ref={canvasRef}
        className={`prime-summon-canvas${canvasVisible ? ' is-visible' : ''}${
          handingOff ? ' is-handoff' : ''
        }`}
        data-testid="prime-summoning-canvas"
      />
    </div>
  );
}

export default PrimeSummoningIntro;
